import { HttpClient, HttpParams } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';

import { firstValueFrom } from 'rxjs';

import { toAppError } from '../../../shared/utils/app-error.util';
import type { AppError } from '../../interfaces/app-error.interface';
import type {
  BaseStoreNebula,
  NebulaStoresPage,
  StoreReplicationResult,
  StoreReplicationTargetResult,
  StoresComparisonParams,
  StoresComparisonResponse,
  StoresReplicationPayload,
} from '../../interfaces/store-comparison.interface';
import { ClusterService } from '../context/cluster.service';
import { translateNow } from '../i18n/translate-now';

/**
 * Store-identity/location fields — replicating these would overwrite a store's own
 * identity or physical fleet assignment, so they can never be sent in replicateParams.
 * Source of truth: coulson-ui's `nonComparableFields` (stores-comparison-filter.tsx).
 * `attributes` is Tesseract's own identity/location blob (name/address/lat/long,
 * see TesseractAttributes in store-comparison.interface.ts) — the exact same role
 * `location` plays for Nebula.
 */
export const NON_COMPARABLE_FIELDS = [
  'id',
  'clientReference',
  'clientId',
  'client_id',
  'location',
  'fleetConfigurations',
  'attributes',
] as const;
export type NonComparableField = (typeof NON_COMPARABLE_FIELDS)[number];

/** Envelope every Coulson endpoint wraps its payload in. */
interface CoulsonEnvelope<T> {
  processId: string;
  message: string;
  operationPayload: T;
}

interface CoulsonReplicationItem {
  message?: string;
  nebulaResponse?: { data?: { id?: string } };
  storeId?: string;
  id?: string;
}

interface CoulsonReplicationSystemResult {
  success?: CoulsonReplicationItem[];
  errors?: CoulsonReplicationItem[];
}

/**
 * Throws if any replicateParams entry targets a non-comparable (identity/location) field.
 * `replicateParams` arrives with the system prefix already stripped (see
 * store-comparison.component.ts's `stripSystemPrefix`), so a field must be matched
 * either as a bare exact name (`location`, `attributes`) or as the suffix of a
 * compound field name that itself contains the word (`client_id`, `nebula_id`) —
 * suffix-only matching never catches a bare exact name.
 */
export function assertReplicableFields(replicateParams: string[]): void {
  const offending = replicateParams.filter((key) =>
    NON_COMPARABLE_FIELDS.some((field) => key === field || key.endsWith(`_${field}`)),
  );
  if (offending.length) {
    throw new Error(translateNow('storeComparison.errors.nonComparable', { fields: offending.join(', ') }));
  }
}

const LIST_PAGE_SIZE = 100;

/** Calls Coulson's existing store-comparison endpoints as-is (catalog/stores, gateway/nebula/capacity/stores). */
@Injectable({ providedIn: 'root' })
export class StoreComparisonApiService {
  private readonly http = inject(HttpClient);
  private readonly clusterService = inject(ClusterService);
  private readonly baseUrl = computed(() => this.clusterService.coulsonBaseUrl());

  readonly listStoresLoading = signal(false);
  readonly listStoresError = signal<AppError | null>(null);
  readonly compareLoading = signal(false);
  readonly compareError = signal<AppError | null>(null);
  readonly replicateLoading = signal(false);
  readonly replicateError = signal<AppError | null>(null);

  /** GET gateway/nebula/capacity/stores — all of a client's Nebula stores, auto-paginated. */
  async listClientStores(clientId: string): Promise<BaseStoreNebula[]> {
    this.listStoresLoading.set(true);
    this.listStoresError.set(null);
    try {
      const first = await this.fetchStoresPage(clientId, 0);
      if (first.numberOfPages <= 1) return first.stores;
      const rest = await Promise.all(
        Array.from({ length: first.numberOfPages - 1 }, (_, i) =>
          this.fetchStoresPage(clientId, (i + 1) * LIST_PAGE_SIZE),
        ),
      );
      return [...first.stores, ...rest.flatMap((r) => r.stores)];
    } catch (err) {
      this.listStoresError.set(toAppError(err, { source: 'coulson' }));
      return [];
    } finally {
      this.listStoresLoading.set(false);
    }
  }

  private fetchStoresPage(clientId: string, offset: number): Promise<NebulaStoresPage> {
    const params = new HttpParams()
      .set('clientId', clientId)
      .set('limit', LIST_PAGE_SIZE)
      .set('offset', offset);
    return firstValueFrom(
      this.http.get<NebulaStoresPage>(`${this.baseUrl()}gateway/nebula/capacity/stores`, {
        params,
      }),
    );
  }

  /** GET catalog/stores — compare a base store against the client's other stores. */
  async compareStores(params: StoresComparisonParams): Promise<StoresComparisonResponse | null> {
    this.compareLoading.set(true);
    this.compareError.set(null);
    try {
      let httpParams = new HttpParams()
        .set('clientId', params.clientId)
        .set('baseStoreId', params.baseStoreId)
        .set('limit', params.limit)
        .set('offset', params.offset);
      params.systems.forEach((s) => (httpParams = httpParams.append('systems', s)));
      params.stores?.forEach((s) => (httpParams = httpParams.append('stores', s)));
      if (params.fields?.length) httpParams = httpParams.set('fields', params.fields.join(','));

      const res = await firstValueFrom(
        this.http.get<CoulsonEnvelope<StoresComparisonResponse>>(
          `${this.baseUrl()}catalog/stores`,
          {
            params: httpParams,
          },
        ),
      );
      return res.operationPayload;
    } catch (err) {
      this.compareError.set(toAppError(err, { source: 'coulson' }));
      return null;
    } finally {
      this.compareLoading.set(false);
    }
  }

  /**
   * PATCH catalog/stores — replicate replicateParams from the base store to `stores`.
   * Force-appends 'nebula' to systems, matching Coulson's frontend exactly.
   */
  async replicateStoreFields(
    payload: StoresReplicationPayload,
  ): Promise<StoreReplicationResult | null> {
    assertReplicableFields(payload.replicateParams);
    this.replicateLoading.set(true);
    this.replicateError.set(null);
    try {
      const response = await firstValueFrom(
        this.http.patch(`${this.baseUrl()}catalog/stores`, {
          ...payload,
          systems: [...payload.systems, 'nebula'],
        }),
      );
      return this.normalizeReplicationResult(response, payload.stores ?? []);
    } catch (err) {
      this.replicateError.set(toAppError(err, { source: 'coulson' }));
      return null;
    } finally {
      this.replicateLoading.set(false);
    }
  }

  /**
   * Coulson groups outcomes by system. Successful Nebula entries consistently carry
   * `nebulaResponse.data.id`; error payloads have varied over time, so accept common
   * id fields and leave unmatched destinations as an explicit unknown failure.
   */
  private normalizeReplicationResult(
    response: unknown,
    targetIds: string[],
  ): StoreReplicationResult {
    const payload = (response as CoulsonEnvelope<Record<string, CoulsonReplicationSystemResult>>)
      ?.operationPayload;
    const outcomes = new Map<string, StoreReplicationTargetResult>();
    for (const systemResult of Object.values(payload ?? {})) {
      for (const item of systemResult.success ?? []) this.recordOutcome(outcomes, item, true);
      for (const item of systemResult.errors ?? []) this.recordOutcome(outcomes, item, false);
    }
    return {
      targets: targetIds.map(
        (storeId) =>
          outcomes.get(storeId) ?? {
            storeId,
            ok: false,
            message: translateNow('storeComparison.errors.noResult'),
          },
      ),
    };
  }

  private recordOutcome(
    outcomes: Map<string, StoreReplicationTargetResult>,
    item: CoulsonReplicationItem,
    ok: boolean,
  ): void {
    const storeId = item.nebulaResponse?.data?.id ?? item.storeId ?? item.id;
    if (!storeId) return;
    const existing = outcomes.get(storeId);
    if (existing && !existing.ok && ok) return;
    outcomes.set(storeId, {
      storeId,
      ok,
      message:
        item.message ??
        translateNow(ok ? 'storeComparison.results.updated' : 'storeComparison.results.updateFailed'),
    });
  }
}
